import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

interface DialogState {
  open: boolean;
  type: 'custom' | 'confirmation';
  title?: React.ReactNode;
  titleTooltip?: React.ReactNode;
  showActiveBusinessUnit?: boolean;
  confirmationText?: React.ReactNode;
  confirmButtonText?: React.ReactNode;
  content: React.ReactNode;
  contentLoading: boolean;
  contentLoadingText?: React.ReactNode;
  loading: boolean;
  fullScreen: boolean;
  item: { name: string; id: string };
  currentFocusTarget: string | null;
  lastFocusedElement: HTMLElement | null;
}

interface DialogActions {
  setLoading: (loading: boolean) => void;
  setContentLoading: (loading: boolean) => void;
  onConfirm: () => void;
  openDialog: (action: {
    type: 'custom' | 'confirmation';
    confirmButtonText?: React.ReactNode;
    confirmationText?: React.ReactNode;
    item?: { name: string; id: string };
    title?: React.ReactNode;
    titleTooltip?: React.ReactNode;
    contentLoadingText?: React.ReactNode;
    showActiveBusinessUnit?: boolean;
    fullScreen?: boolean;
    onConfirm?: () => void;
    element?: React.ReactNode;
  }) => void;
  closeDialog: () => void;
  setCurrentFocusTarget: (focusKey: string | null) => void;
}

const initialState: DialogState = {
  open: false,
  type: 'custom',
  confirmButtonText: undefined,
  confirmationText: undefined,
  title: undefined,
  titleTooltip: undefined,
  showActiveBusinessUnit: false,
  loading: false,
  fullScreen: false,
  content: undefined,
  contentLoading: false,
  contentLoadingText: undefined,
  item: { name: '', id: '' },
  currentFocusTarget: null,
  lastFocusedElement: null,
};

export const useDialogStore = create<DialogState & DialogActions>()(
  devtools((set) => ({
    ...initialState,
    onConfirm: () => {},
    setLoading: (loading) => set((state) => ({ ...state, loading })),
    setContentLoading: (loading) => set((state) => ({ ...state, contentLoading: loading })),
    openDialog: ({
      type,
      item,
      title,
      titleTooltip,
      contentLoadingText,
      showActiveBusinessUnit,
      fullScreen,
      element,
      onConfirm,
      confirmationText,
      confirmButtonText,
    }) =>
      set(() => {
        // Capture the currently focused element before opening the dialog
        const lastFocusedElement = document.activeElement as HTMLElement;
        return {
          open: true,
          item,
          title,
          titleTooltip,
          showActiveBusinessUnit,
          type,
          fullScreen,
          confirmButtonText,
          confirmationText,
          content: element,
          onConfirm,
          contentLoading: false,
          contentLoadingText,
          lastFocusedElement,
        };
      }),
    closeDialog: () =>
      set((state) => ({
        ...state,
        ...initialState,
        currentFocusTarget: state.currentFocusTarget,
        lastFocusedElement: state.lastFocusedElement,
      })),
    setCurrentFocusTarget: (focusKey) =>
      set((state) => ({
        ...state,
        currentFocusTarget: focusKey,
      })),
  })),
);
